import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  LayoutDashboard, ArrowRightLeft, Receipt, Calculator, PieChart, FileCheck2,
  Settings as SettingsIcon, ShieldAlert, MessageCircle, LogOut, PanelLeftClose, PanelLeftOpen, Percent,
  ScanLine, FileSpreadsheet, FileText, Images, Wallet, Landmark, FileBarChart, ChevronDown, Lock,
} from 'lucide-react';
import { WHATSAPP_LINK } from '../utils/constants';

const RANK = { free: 0, plus: 1, pro: 2, business: 3 };

const NAV = [
  { type: 'item', icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', req: 'free', testId: 'nav-dashboard' },
  { type: 'group', key: 'money', icon: Wallet, label: 'Money', children: [
    { icon: ArrowRightLeft, label: 'Transactions', path: '/transactions', req: 'free', testId: 'nav-transactions' },
    { icon: Receipt, label: 'Bills', path: '/bills', req: 'free', testId: 'nav-bills' },
    { icon: FileText, label: 'Invoices', path: '/invoices', req: 'pro', testId: 'nav-invoices' },
  ] },
  { type: 'group', key: 'receipts', icon: ScanLine, label: 'Receipts & Scanning', children: [
    { icon: ScanLine, label: 'Scan Center', path: '/scan-center', req: 'free', testId: 'nav-scan-center' },
    { icon: Images, label: 'Receipts', path: '/receipts', req: 'free', testId: 'nav-receipts' },
  ] },
  { type: 'group', key: 'taxes', icon: Landmark, label: 'Taxes', children: [
    { icon: PieChart, label: 'Tax Summary', path: '/tax-summary', req: 'pro', testId: 'nav-tax-summary' },
    { icon: Calculator, label: 'Tax Planner', path: '/tax-planner', req: 'pro', testId: 'nav-tax-planner' },
    { icon: FileCheck2, label: 'Tax Filing', path: '/filing', req: 'pro', testId: 'nav-filing' },
    { icon: Percent, label: 'Tax Calculator', path: '/tools/1099-tax-calculator', req: 'free', testId: 'nav-tax-calculator' },
  ] },
  { type: 'group', key: 'reports', icon: FileBarChart, label: 'Reports & Export', children: [
    { icon: FileBarChart, label: 'Financials', path: '/financials', req: 'pro', testId: 'nav-financials' },
    { icon: FileSpreadsheet, label: 'Export Center', path: '/export-center', req: 'pro', testId: 'nav-export-center' },
  ] },
  { type: 'item', icon: SettingsIcon, label: 'Settings', path: '/settings', req: 'free', testId: 'nav-settings' },
];

export const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [pinned, setPinned] = useState(() => localStorage.getItem('sidebarPinned') === '1');
  const [open, setOpen] = useState({});

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) { try { setUser(JSON.parse(u)); } catch (e) { /* noop */ } }
    // auto-open the group containing the active route
    const active = {};
    NAV.forEach((n) => { if (n.type === 'group' && n.children.some((c) => c.path === location.pathname)) active[n.key] = true; });
    setOpen(active);
  }, [location.pathname]);

  const userRank = user?.is_admin || user?.is_pro ? RANK.pro : (RANK[user?.plan] ?? 0);
  const unlocked = (req) => user?.is_admin || userRank >= (RANK[req] ?? 0);
  const goItem = (it) => navigate(unlocked(it.req) ? it.path : '/upgrade');
  const togglePin = () => setPinned((p) => { const n = !p; localStorage.setItem('sidebarPinned', n ? '1' : '0'); return n; });
  const handleLogout = () => { localStorage.removeItem('token'); localStorage.removeItem('user'); navigate('/'); toast.success('Logged out successfully'); };

  const nav = user?.is_admin
    ? [...NAV, { type: 'item', icon: ShieldAlert, label: 'Admin', path: '/admin', req: 'free', testId: 'nav-admin' }]
    : NAV;

  const asideWidth = pinned ? 'w-64' : 'w-20 hover:w-64';
  const labelCls = pinned ? 'inline' : 'hidden group-hover:inline';
  const rowAlign = pinned ? 'justify-start' : 'justify-center group-hover:justify-start';

  const Row = ({ it, child }) => {
    const active = location.pathname === it.path;
    const Icon = it.icon;
    const locked = !unlocked(it.req);
    return (
      <button
        onClick={() => goItem(it)}
        data-testid={it.testId}
        title={it.label}
        className={`w-full flex items-center gap-3 ${child ? 'pl-9 pr-3 py-2' : 'pl-3 pr-3 py-3'} rounded-lg border-l-4 ${rowAlign} transition-colors duration-200 ${
          active ? 'bg-white/10 border-[#84CC16] text-[#84CC16]' : 'border-transparent text-slate-200 hover:bg-white/5 hover:text-white'
        }`}
      >
        <Icon className={`${child ? 'h-4 w-4' : 'h-5 w-5'} shrink-0`} />
        <span className={`whitespace-nowrap text-sm font-medium flex-1 text-left ${labelCls}`}>{it.label}</span>
        {locked && <Lock className={`h-3.5 w-3.5 text-[#84CC16] shrink-0 ${labelCls}`} />}
      </button>
    );
  };

  return (
    <aside
      className={`hidden md:flex flex-col shrink-0 ${asideWidth} transition-all duration-300 ease-in-out bg-gradient-to-b from-[#064E3B] via-[#05513B] to-[#043A2C] dark:from-gray-900 dark:via-gray-900 dark:to-gray-950 text-white h-screen sticky top-0 z-30 overflow-hidden group border-r border-white/5`}
      data-testid="app-sidebar"
      data-pinned={pinned ? 'true' : 'false'}
    >
      <div className="flex items-center justify-between h-16 px-4 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-xl bg-white/10 flex items-center justify-center shrink-0 p-1.5">
            <img src="/logo-mark.png" alt="MoneyFeed" className="h-full w-auto" />
          </div>
          <span className={`font-bold text-lg whitespace-nowrap ${labelCls}`}>MoneyFeed</span>
        </div>
        <button onClick={togglePin} data-testid="sidebar-pin-toggle" title={pinned ? 'Unpin sidebar' : 'Pin sidebar open'}
          className={`text-slate-300 hover:text-[#84CC16] transition-colors shrink-0 ${pinned ? '' : 'hidden group-hover:block'}`}>
          {pinned ? <PanelLeftClose className="h-5 w-5" /> : <PanelLeftOpen className="h-5 w-5" />}
        </button>
      </div>

      <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
        {nav.map((n) => {
          if (n.type === 'item') return <Row key={n.path} it={n} />;
          const GIcon = n.icon;
          const isOpen = !!open[n.key];
          const groupActive = n.children.some((c) => c.path === location.pathname);
          return (
            <div key={n.key}>
              <button
                onClick={() => setOpen((o) => ({ ...o, [n.key]: !o[n.key] }))}
                data-testid={`nav-group-${n.key}`}
                title={n.label}
                className={`w-full flex items-center gap-3 pl-3 pr-3 py-3 rounded-lg border-l-4 ${rowAlign} transition-colors duration-200 ${
                  groupActive ? 'border-[#84CC16] text-[#84CC16]' : 'border-transparent text-slate-200 hover:bg-white/5 hover:text-white'
                }`}
              >
                <GIcon className="h-5 w-5 shrink-0" />
                <span className={`whitespace-nowrap text-sm font-semibold flex-1 text-left ${labelCls}`}>{n.label}</span>
                <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''} ${labelCls}`} />
              </button>
              {isOpen && (
                <div className="mt-0.5 space-y-0.5" data-testid={`nav-subitems-${n.key}`}>
                  {n.children.map((c) => <Row key={c.path} it={c} child />)}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="px-2 pb-4 space-y-1 shrink-0">
        <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="nav-whatsapp-cta" title="Chat on WhatsApp"
          className={`w-full flex items-center gap-4 pl-3 pr-3 py-3 rounded-lg bg-[#25D366] hover:bg-[#1FAE54] text-white font-semibold ${rowAlign} transition-colors duration-200`}>
          <MessageCircle className="h-5 w-5 shrink-0" />
          <span className={`whitespace-nowrap text-sm ${labelCls}`}>Chat on WhatsApp</span>
        </a>
        <button onClick={handleLogout} data-testid="nav-logout" title="Logout"
          className={`w-full flex items-center gap-4 pl-3 pr-3 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white ${rowAlign} transition-colors duration-200`}>
          <LogOut className="h-5 w-5 shrink-0" />
          <span className={`whitespace-nowrap text-sm font-medium ${labelCls}`}>Logout</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
